/**
 * The graph theory behind the game, with no DOM in sight.
 *
 * A figure can be drawn in one stroke exactly when it is connected and has
 * either no odd-degree vertices (a closed circuit: start anywhere) or exactly
 * two (an open path: you must start at one of them). Four or more and there is
 * no way at all. That single fact decides which figures ship, where the hint
 * points, and why a child who starts in the wrong place can never finish.
 */

export interface Figure {
  id: string;
  /** Points on a 0-100 grid; the canvas scales them. */
  vertices: Array<[number, number]>;
  /** Pairs of vertex indices. No self-loops, no repeats. */
  edges: Array<[number, number]>;
}

export function degrees(figure: Figure): number[] {
  const count = new Array<number>(figure.vertices.length).fill(0);
  for (const [a, b] of figure.edges) {
    count[a] += 1;
    count[b] += 1;
  }
  return count;
}

export function oddVertices(figure: Figure): number[] {
  return degrees(figure).flatMap((degree, index) => (degree % 2 === 1 ? [index] : []));
}

function adjacencyList(figure: Figure): Array<Array<{ to: number; edge: number }>> {
  const list: Array<Array<{ to: number; edge: number }>> = figure.vertices.map(() => []);
  figure.edges.forEach(([a, b], edge) => {
    list[a].push({ to: b, edge });
    list[b].push({ to: a, edge });
  });
  return list;
}

/** Every vertex reachable from the first one, and none left stranded. */
export function isConnected(figure: Figure): boolean {
  const total = figure.vertices.length;
  if (total === 0) return false;
  const adjacency = adjacencyList(figure);
  if (adjacency.some((list) => list.length === 0)) return false;

  const seen = new Set<number>([0]);
  const stack = [0];
  while (stack.length > 0) {
    const current = stack.pop() as number;
    for (const { to } of adjacency[current]) {
      if (!seen.has(to)) {
        seen.add(to);
        stack.push(to);
      }
    }
  }
  return seen.size === total;
}

export function isSolvable(figure: Figure): boolean {
  if (figure.edges.length === 0 || !isConnected(figure)) return false;
  const odd = oddVertices(figure).length;
  return odd === 0 || odd === 2;
}

/**
 * Where a child is allowed to begin. Two odd vertices means those two and
 * nowhere else; none means anywhere works. This is what the hint flashes.
 */
export function startVertices(figure: Figure): number[] {
  const odd = oddVertices(figure);
  if (odd.length === 2) return odd;
  return figure.vertices.map((_, index) => index);
}

export function neighbours(figure: Figure, vertex: number): Array<{ to: number; edge: number }> {
  return adjacencyList(figure)[vertex] ?? [];
}

/**
 * One legal solution as a list of vertices, or null when none exists.
 * Hierholzer's algorithm: walk until stuck, then splice in detours from any
 * vertex that still has unused edges.
 *
 * The game never shows this to a child — it exists so the test suite can prove
 * each figure is solvable by solving it, rather than trusting a degree count.
 */
export function findEulerPath(figure: Figure): number[] | null {
  if (!isSolvable(figure)) return null;

  const adjacency = adjacencyList(figure);
  const used = new Array<boolean>(figure.edges.length).fill(false);
  const odd = oddVertices(figure);
  const start = odd.length === 2 ? odd[0] : 0;

  const stack = [start];
  const trail: number[] = [];
  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const next = adjacency[current].find((step) => !used[step.edge]);
    if (next) {
      used[next.edge] = true;
      stack.push(next.to);
    } else {
      trail.push(stack.pop() as number);
    }
  }

  trail.reverse();
  return trail.length === figure.edges.length + 1 ? trail : null;
}
